import * as React from "react"
import { Container, Modal, Form, Image, Row, Col, Table, InputGroup } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import ActionButton from "../ActionButton"
import CalculatePasswordScore, { PasswordScore, ScoreStatus } from "./CalculatePasswordScore"
import passwordStatus_Exceed from "../../assets/images/img_status_exceed.png"
import passwordStatus_Pass from "../../assets/images/img_status_pass.png"
import passwordStatus_Warn from "../../assets/images/img_status_warn.png"
import passwordStatus_Fail from "../../assets/images/img_status_fail.png"
import "../../assets/scss/App.scss"
import "../../assets/scss/SetPassword.scss"
import PasswordSetModel from "../../api/Invitation/Model/PasswordSetModel"
import PasswordSet from "../../api/Invitation/PasswordSet"
import Checkbox from "../Checkbox"
import { APIError } from "../../api/ObservableFromFetch"

interface SetPasswordProps {
  PIN: string
  userName: string
  OutboxAN: number
  onSave: (password: string) => void
}

interface SetPasswordState {
  saving: boolean
  password: string
  passwordScore?: PasswordScore
  showPassword: boolean
}

export default class SetPassword extends React.PureComponent<SetPasswordProps, SetPasswordState> {
  state: SetPasswordState = {
    saving: false,
    password: "",
    passwordScore: CalculatePasswordScore(""),
    showPassword: false
  }
  statusImages: { [key in keyof typeof ScoreStatus]: string } = {
    [ScoreStatus.exceed]: passwordStatus_Exceed,
    [ScoreStatus.pass]: passwordStatus_Pass,
    [ScoreStatus.warn]: passwordStatus_Warn,
    [ScoreStatus.fail]: passwordStatus_Fail
  }

  saveSubscription?: Subscription

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  isValid = () => this.state.password.trim().length > 8

  handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value
    this.setState({ password: password, passwordScore: CalculatePasswordScore(password) })
  }

  handleSetPasswordClick = () => {
    this.setState({ saving: true })
    const password: PasswordSetModel = {
      OutboxAN: this.props.OutboxAN,
      Password: this.state.password,
      UserName: this.props.userName,
      PIN: this.props.PIN,
      Link: window.location.href
    }
    //TODO check if we need to call haveIbeenPwnedURL: string = 'https://api.pwnedpasswords.com/range/';
    this.saveSubscription = PasswordSet(password).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.setState({ password: "", showPassword: false })
        this.props.onSave(password.Password)
      },
      error => {
        this.setState({ saving: false })
        this.handleAPIError(error)
      }
    )
  }

  handleAPIError = (error: Error | APIError) => {
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  passwordComplexity = (score: number): string => {
    if (score < 20) {
      return "Very Weak"
    } else if (score < 40) {
      return "Weak"
    } else if (score < 60) {
      return "Good"
    } else if (score < 80) {
      return "Strong"
    } else {
      return "Very Strong"
    }
  }

  statusImage = (status: ScoreStatus | undefined) => status && <Image src={this.statusImages[status]} />
  scoreValue = (score: number | undefined, scoreSign: string) => (score || 0) > 0 ? `${scoreSign}${score}` : `${score}`

  render() {
    const { passwordScore, showPassword } = this.state

    const requirements = <Table bordered className="requirements">
      <thead><tr><th>Minimum Requirements</th></tr></thead>
      <tbody><tr><td>
        <ul className="m-0">
          <li>Minimum 8 characters in length</li>
          <li>Contains 3/4 of the following items:<br />
            - Uppercase Letters<br />
            - Lowercase Letters<br />
            - Numbers<br />
            - Symbols<br />
          </li>
        </ul>
      </td></tr></tbody>
    </Table>

    const totalScore = Math.max(0, Math.min(100, passwordScore?.calculatedScore || 0))
    return (
      <Modal backdrop="static" size="xl" show>
        <Modal.Header>
          <Modal.Title>SET PASSWORD</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid className="set-password">
            <Row>
              <Col sm={6}>
                <div>Your User ID is:</div>
                <div className="vertical-center pl-4">{this.props.userName}</div>
              </Col>
              <Col sm={6} className="d-none d-xl-block">{requirements}</Col>
            </Row>
            <Row>
              <Col className="mb-4">
                <span className="float-left">Please set a password for this account:</span>
                <span className="float-right">Characters: {this.state.password.length}/32</span>
                <InputGroup>
                  <Form.Control type={showPassword ? "text" : "password"} onChange={this.handlePasswordChange} maxLength={32} />
                  <Checkbox className="show-password" id="show-password" label="Show Password" checked={showPassword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ showPassword: e.target.checked })} value="1" />
                </InputGroup>
              </Col>
            </Row>
            <Row>
              <Col xl={12}>
                <Table bordered className="score-total">
                  <tbody>
                    <tr>
                      <td>Score:</td>
                      <td>
                        <div className="score-background" style={{ backgroundPositionX: totalScore + "%" }}>{totalScore}%</div>
                      </td>
                    </tr>
                    <tr>
                      <td>Complexity:</td>
                      <td>{this.passwordComplexity(this.state.passwordScore?.calculatedScore || 0)}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
            <Row className="d-block d-xl-none"><Col>{requirements}</Col></Row>
          </Container >
        </Modal.Body >
        <Modal.Footer>
          <ActionButton variant="primary" disabled={!this.isValid()} onClick={this.handleSetPasswordClick} spin={this.state.saving} disableOnSpin>Set Password</ActionButton>
        </Modal.Footer>
      </Modal>
    )
  }
}
